import React, { useId } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { DROPDOWN_NARROW_SX, DROPDOWN_SX, DROPDOWN_WIDE_SX } from '../../constants/formLayout';

/**
 * `narrow`: a short enum filter (status, type, difficulty, role, severity), 160px.
 * `wide`: a person, track or other long-name picker, 240px.
 * Both are filter-row sizes: a fixed width and 14px text.
 * `field`: a form field (DROPDOWN_SX: 100% of its slot up to the cap, never full width).
 */
export type DropdownWidth = 'narrow' | 'wide' | 'field';

export interface DropdownOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

type DataAttributes = { [attribute: `data-${string}`]: string | undefined };

export interface DropdownProps extends DataAttributes {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<DropdownOption>;
  /** Floating label on the border, for filter rows and MUI forms. */
  label?: string;
  /** The accessible name when no label is visible (or the visible one is not an element). */
  ariaLabel?: string;
  /** Id of a visible label outside the control (a form's label above or beside it). */
  labelledBy?: string;
  /** Id of the combobox element itself. */
  id?: string;
  width?: DropdownWidth;
  size?: 'small' | 'medium';
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  /** Shown, muted, while `value` is '' and no option has the value ''. */
  placeholder?: string;
}

const WIDTH_SX: Record<DropdownWidth, object> = {
  narrow: DROPDOWN_NARROW_SX,
  wide: DROPDOWN_WIDE_SX,
  field: DROPDOWN_SX,
};

/**
 * The open menu sits above every overlay in the app: the custom Tailwind
 * dialogs go up to z-[1700] and the review toast to z-[1800], while MUI's own
 * menu z-index (1300) would open behind them.
 */
const MENU_Z_INDEX = 1900;

/**
 * The one dropdown on every page: an MUI outlined Select with MUI's default
 * arrow. Named Dropdown so it never collides with MUI's own `Select` import.
 * Complex pickers (multiple, chips, grouped options, custom renderValue) stay
 * raw MUI `Select` but follow the same size, width and label rules
 * (DESIGN_SYSTEM.md, "Component contracts that changed on purpose").
 */
export const Dropdown: React.FC<DropdownProps> = ({
  value,
  onChange,
  options,
  label,
  ariaLabel,
  labelledBy,
  id,
  width = 'field',
  size = 'small',
  disabled,
  required,
  autoFocus,
  placeholder,
  ...rest
}) => {
  const generatedId = useId();
  const floatingLabelId = label ? `${generatedId}-label` : undefined;
  const labelId = labelledBy ?? floatingLabelId;

  const dataAttributes = Object.fromEntries(
    Object.entries(rest).filter(([attribute]) => attribute.startsWith('data-'))
  );

  const hasEmptyOption = options.some((option) => option.value === '');
  // A value no option carries reads as nothing chosen, as a native select did.
  const selectValue = options.some((option) => option.value === value) ? value : '';
  const displayEmpty = hasEmptyOption || Boolean(placeholder);

  const renderValue = placeholder && !hasEmptyOption
    ? (selected: string) => selected === ''
      ? <Box component="span" sx={{ color: 'text.secondary' }}>{placeholder}</Box>
      : options.find((option) => option.value === selected)?.label ?? selected
    : undefined;

  return (
    <FormControl
      size={size}
      disabled={disabled}
      sx={WIDTH_SX[width]}
      {...dataAttributes}
    >
      {label && (
        <InputLabel id={floatingLabelId} shrink={displayEmpty ? true : undefined}>
          {label}
        </InputLabel>
      )}
      <Select<string>
        id={id}
        labelId={labelId}
        label={label}
        value={selectValue}
        onChange={(event: SelectChangeEvent<string>) => onChange(String(event.target.value))}
        displayEmpty={displayEmpty}
        renderValue={renderValue}
        required={required}
        autoFocus={autoFocus}
        // The name is the label alone, not "label + current value" as MUI
        // builds it when the combobox carries its own id.
        SelectDisplayProps={{
          'aria-labelledby': labelId,
          'aria-label': labelId ? undefined : ariaLabel,
        } as React.HTMLAttributes<HTMLDivElement>}
        MenuProps={{
          sx: { zIndex: MENU_Z_INDEX },
          slotProps: {
            list: {
              'aria-label': labelId ? undefined : ariaLabel,
              // A filter's menu reads at the filter's own 14px.
              sx: width === 'field' ? undefined : { '& .MuiMenuItem-root': { fontSize: 'var(--text-body-s)' } },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default Dropdown;
